import { useContentResource } from "@ctv/core";

import {
  Checkbox,
  InputDropdownRHF,
  InputRHF,
  Text,
  ThousandSeparatorInput,
  Token,
  useLocale,
  View,
} from "@ctv/shared-core/src";

import { Fragment, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { StyleSheet } from "react-native";
import { BILLING_CYCLES, TERMS_OF_PAYMENTS } from "../../../_constants";
import { RegistrationFormValue } from "../../../types";
import { FieldRow } from "../../FieldRow";
import InformationDirectorAttorney from "./InformationDirectorAttorney";

type CurrentTarget = { value: string };

export default function InvoiceForm() {
  const { CorporateRegistration: cr, CorporateRegistrationDropdown: crd } =
    useContentResource();
  const { watch, setValue, getValues } =
    useFormContext<RegistrationFormValue>();

  const {
    legalBillingAddress,
    legalBillingCity,
    legalBillingPostalCode,
    legalFiscalAddress,
    legalFiscalCity,
    legalFiscalPostalCode,
  } = watch();

  const locale = useLocale();

  const [copyBillingAddress, setCopyBillingAddress] = useState<boolean>(
    legalBillingAddress === legalFiscalAddress &&
      legalBillingCity === legalFiscalCity &&
      legalBillingPostalCode === legalFiscalPostalCode
  );

  const billingCycles = BILLING_CYCLES.map((cycle) => ({
    label: crd[cycle],
    value: cycle,
  }));

  const termsOfPayments = Object.entries(TERMS_OF_PAYMENTS).map((value) => {
    const [TERMS_OF_PAY, DAY] = value;
    return {
      label: crd[TERMS_OF_PAY],
      value: DAY,
    };
  });

  return (
    <Fragment>
      <View style={styles.divider} />
      <View style={[styles.content, styles.zIndex2]}>
        <InformationDirectorAttorney />
      </View>
      <View style={styles.divider} />
      <View style={[styles.content, styles.zIndex1]}>
        <Text style={styles.marginBottomM} variant="title-1">
          {cr.paymentProposalTitle}
        </Text>
        <FieldRow
          style={styles.fieldRow}
          label={cr.paymentProposalServiceFeeLabel}
          tooltip={cr.paymentProposalServiceFeeTooltip}
          required
        >
          <View row align="start">
            <InputRHF
              name="serviceFee"
              onBlur={(value, onChange) => onChange(value.trim())}
              style={styles.flex1}
            />
            <Text style={styles.percentTag}>%</Text>
          </View>
        </FieldRow>
        <FieldRow
          style={[styles.fieldRow, styles.zIndex2]}
          label={cr.paymentProposalBillingCycleLabel}
          required
        >
          <InputDropdownRHF
            name="billingCycle"
            style={styles.flex1}
            items={billingCycles}
            editable
            searchable
            labelAsInputValue
          />
        </FieldRow>
        <FieldRow
          style={[styles.fieldRow, styles.zIndex1]}
          label={cr.paymentProposalTermsOfPaymentLabel}
          required
        >
          <InputDropdownRHF
            editable
            searchable
            labelAsInputValue
            style={styles.flex1}
            name="termsOfPayment"
            items={termsOfPayments}
          />
        </FieldRow>
        <FieldRow
          style={[styles.marginBottomM, styles.fieldRow]}
          label={cr.paymentProposalCredtLimitLabel}
          tooltip={cr.paymentProposalCredtLimitTooltip}
          tooltipPosition="bottom"
          required
        >
          <Controller
            name="creditLimit"
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <ThousandSeparatorInput
                error={error?.message}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                locale={locale}
                placeholder="IDR..."
              />
            )}
          />
        </FieldRow>
      </View>
      <View style={styles.divider} />
      <View style={[styles.content]}>
        <Text style={styles.marginBottomM} variant="title-1">
          {cr.legalDetailsTitle}
        </Text>
        <FieldRow
          style={styles.marginBottomM}
          label={cr.legalDetailsNpwpNumberLabel}
          required
        >
          <InputRHF
            name="legalNpwpNumber"
            onBlur={(value, onChange) => onChange(value.trim())}
            style={styles.flex1}
          />
        </FieldRow>
        <FieldRow
          style={styles.marginBottomM}
          label={cr.legalDetailsSppkpNumberLabel}
          subLabel={cr.legalDetailsSppkpNumberSubLabel}
          required
        >
          <InputRHF
            name="legalSppkpNumber"
            onBlur={(value, onChange) => onChange(value.trim())}
            style={styles.flex1}
          />
        </FieldRow>
        <FieldRow
          style={styles.marginBottomM}
          label={cr.legalDetailsCorporateNameLabel}
          required
        >
          <InputRHF
            name="legalName"
            onBlur={(value, onChange) => onChange(value.trim())}
            style={styles.flex1}
          />
        </FieldRow>
        <FieldRow
          style={styles.marginBottomM}
          label={cr.legalDetailsBusinessAddressLabel}
          required
        >
          <InputRHF
            multiline
            numberOfLines={2}
            style={styles.flex1}
            name="legalFiscalAddress"
            placeholder={cr.legalDetailsAddressPlaceholder}
            onBlur={(value, onChange) => onChange(value.trim())}
            onChange={
              copyBillingAddress
                ? (e) =>
                    setValue(
                      "legalBillingAddress",
                      (e.currentTarget as unknown as CurrentTarget).value
                    )
                : undefined
            }
          />
          <View row style={styles.margintTopXS}>
            <InputRHF
              name="legalFiscalCity"
              placeholder={cr.legalDetailsCityPlaceholder}
              style={[styles.flex1, styles.margintRightXS]}
              onBlur={(value, onChange) => onChange(value.trim())}
              onChange={
                copyBillingAddress
                  ? (e) =>
                      setValue(
                        "legalBillingCity",
                        (e.currentTarget as unknown as CurrentTarget).value
                      )
                  : undefined
              }
            />
            <InputRHF
              style={styles.flex1}
              name="legalFiscalPostalCode"
              placeholder={cr.legalDetailsPostalCodePlaceholder}
              onBlur={(value, onChange) => onChange(value.trim())}
              onChange={
                copyBillingAddress
                  ? (e) =>
                      setValue(
                        "legalBillingPostalCode",
                        (e.currentTarget as unknown as CurrentTarget).value
                      )
                  : undefined
              }
            />
          </View>
          <View style={styles.margintTopXS}>
            <Checkbox
              checked={copyBillingAddress}
              onChange={(value) => {
                if (value) {
                  setCopyBillingAddress(value);
                  setValue("legalBillingAddress", legalFiscalAddress);
                  setValue("legalBillingCity", legalFiscalCity);
                  setValue("legalBillingPostalCode", legalFiscalPostalCode);
                  return;
                }
                setCopyBillingAddress(value);
              }}
            >
              <Text variant="ui-tiny" ink="black-secondary">
                {cr.legalDetailsCopyAddressNpwpCheckbox}
              </Text>
            </Checkbox>
          </View>
        </FieldRow>
        <FieldRow
          style={styles.marginBottomM}
          label={cr.legalDetailsBillingAddressLabel}
          required
        >
          <InputRHF
            name="legalBillingAddress"
            onBlur={(value, onChange) => onChange(value.trim())}
            style={styles.flex1}
            multiline
            numberOfLines={2}
            placeholder={cr.legalDetailsAddressPlaceholder}
            disabled={copyBillingAddress}
          />
          <View row style={styles.margintTopXS}>
            <InputRHF
              name="legalBillingCity"
              onBlur={(value, onChange) => onChange(value.trim())}
              style={[styles.flex1, styles.margintRightXS]}
              placeholder={cr.legalDetailsCityPlaceholder}
              disabled={copyBillingAddress}
            />
            <InputRHF
              name="legalBillingPostalCode"
              onBlur={(value, onChange) => onChange(value.trim())}
              style={styles.flex1}
              placeholder={cr.legalDetailsPostalCodePlaceholder}
              disabled={copyBillingAddress}
            />
          </View>
        </FieldRow>
      </View>
    </Fragment>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: Token.spacing.l,
  },
  divider: {
    borderTopColor: Token.color.borderDivide,
    borderTopWidth: Token.borderWidth.thick,
  },
  fieldRow: {
    alignItems: "center",
    marginVertical: Token.spacing.xs,
  },
  percentTag: {
    backgroundColor: Token.color.lightSecondary,
    paddingVertical: Token.spacing.xs,
    paddingHorizontal: Token.spacing.s,
    marginLeft: -Token.spacing.xxs,
    zIndex: 1,
    borderBottomRightRadius: Token.borderRadius.normal,
    borderTopRightRadius: Token.borderRadius.normal,
    userSelect: "none",
  },
  // General styles
  flex1: {
    flex: 1,
    paddingVertical: 0,
  },
  zIndex2: {
    zIndex: 2,
  },
  zIndex1: {
    zIndex: 1,
  },
  margintTopXS: {
    marginTop: Token.spacing.xs,
  },
  marginBottomM: {
    marginBottom: Token.spacing.m,
  },
  margintRightXS: {
    marginRight: Token.spacing.xs,
  },
});
